<template>
  <div class="table-container">
    <div class="text-center loading-overlay" v-if="isLoading">
      <LoadingAnimation/>
    </div>

    <div class="row mt-2 no-gutters evollu-form" v-if="showFilters">
      <div class="row">
        <div class="col-12 col-md-4">
          <range-date-picker
            id="statistics-date-range"
            :label="$t('statistics2.hear.sublabels.period')"
            v-model="filters.dates"
            :placeholder="this.$t('agenda.reports.date_range')"
            :config="{ maxInterval: { value: 12, unit: 'months' } }"
            :clearable="true"
          >
          </range-date-picker>
        </div>

        <div class="col-12 col-md-4">
          <label class="form-label">
            {{ $t('activities_agenda.table.facility') }}
          </label>
          <multiselect
            id="entities-filter"
            v-model="filters.facility"
            :options="readableFacilities"
            track-by="id"
            :customLabel="formatedEntityLabel"
            :placeholder="readableFacilities.length ? $t('activities_agenda.table.facility') : $t('dashboard_new_evollu.values.empty_facilities')"
            :allow-empty="true"
            :multiple="false"
            :internal-search="true"
            :disabled="!readableFacilities.length"
          >

            <template slot="clear">
              <!--<span class="multiselect__clear" v-if="filters.entities.length"
                    @mousedown.prevent.stop="filters.entities = []"></span>-->
              <span class="multiselect__clear" v-if="filters.facility !== null"
                    @mousedown.prevent.stop="filters.facility = null"></span>
            </template>
          </multiselect>
        </div>

        <div class="col-12 col-md-4">
          <label class="form-label">
            {{ $t('checkup_plus.evaluations.appointment_status') }}
          </label>
          <multiselect
            id="entities-filter"
            v-model="filters.appointmentStatus"
            :options="appointmentStatuses"
            track-by="id"
            :placeholder="$t('checkup_plus.evaluations.appointment_status')"
            :customLabel="(val) => $t(`checkup_plus.appointments.statuses.${val.id}`)"
            :allow-empty="true"
            :multiple="false"
            :internal-search="false"
          >

          </multiselect>
        </div>
      </div>
    </div>

    <v-server-table
      :columns="columns"
      :options="tableOptions"
      id="checkupPlusEvaluationsTable"
      ref="checkupPlusEvaluationsTable"
      name="checkupPlusEvaluationsTable"
      @row-click="click => handleRowClick(click.row)">

      <span slot="createdAt" slot-scope="{row}">
        {{ row.createdAt ? moment(row.createdAt).format("YYYY/MM/DD") : "N/A" }}
      </span>
      <span slot="updatedAt" slot-scope="{row}">
        {{ row.updatedAt ? moment(row.updatedAt).format("YYYY/MM/DD") : "N/A" }}
      </span>
      <span slot="begin" slot-scope="{row}">
        {{ row.begin ? moment(row.begin).format("YYYY/MM/DD HH:mm") : "N/A" }}
      </span>
      <span slot="end" slot-scope="{row}">
        {{ row.end ? moment(row.end).format("YYYY/MM/DD HH:mm") : "N/A" }}
      </span>
      <span slot="status" slot-scope="{row}">
        {{ row.status ? $t(`checkup_plus.appointments.statuses.${row.status}`) : "N/A" }}
      </span>
      <span slot="patient" slot-scope="{row}">
        {{ row.patient ? row.patient.name : "N/A" }}
      </span>

      <span slot="tests" slot-scope="{row}">
        <span v-if="row.measurementAggregate && row.measurementAggregate.length > 0">
          <span v-for="id in distinctMeasurementGroupsInAppointment(row.measurementAggregate)">
            <va-popover
              class="ml-1"
              placement="top"
              :message="$t(`checkup_plus.person.rules.rule_creation.measurement_group.${id}`)">
            <i :class="handleIconStr(id)" style="height: 30px; width: 30px; filter: invert(71%) sepia(60%) saturate(1%) hue-rotate(350deg) brightness(99%) contrast(90%);" ></i>
          </va-popover>
          </span>
        </span>
        <span v-else>
          ---
        </span>

      </span>
    </v-server-table>

    <MeasurementDetailsModal
      v-if="selectedAppointment"
      :measurements="selectedAppointment.measurementAggregate ? selectedAppointment.measurementAggregate : []"
      :patientName="selectedAppointment.patient ? selectedAppointment.patient.name : 'N/A'"
      :appointmentFrom="selectedAppointment.begin | localTime('YYYY-MM-DD HH:mm')"
      :appointmentTo="selectedAppointment.end | localTime('YYYY-MM-DD HH:mm')"
      :appointmentId="selectedAppointment.id"
      @cancel="handleMeasurementDetailsModalClose"
      @refreshTable="handleRefreshTable"
    />
  </div>

</template>

<script>
import Multiselect from "vue-multiselect";
import LocalStorageHelper from "@/local-storage/LocalStorageHelper";
import {CHECkUP_PLUS_EVALUATIONS_TABLE} from "@/local-storage/LocalStorageKeys";
import TableQueryMixin from '@/mixins/TableQueryMixin.vue';
import MultiselectLabelFormatter from '@/mixins/MultiselectLabelFormatter.vue';
import ClustersAndEntitiesMixin from '@/mixins/ClustersAndEntitiesMixin.vue';
import RangeDatePicker from '@/components/common/RangeDatePicker.vue';
import LoadingAnimation from "@/components/users/components/Helpers/LoadingAnimation";
import { OneCareService } from '@/api/OneCareService';
import ApiService from '@/api/ApiService';
import axios from "axios";
import MeasurementDetailsModal from '@/components/people-manager/patient/MeasurementDetailsModal.vue';

export default {
  props: {
    activeFilters: {
      type: Number,
      required: true,
      default: 0
    },
    showFilters: {
      type: Boolean,
      required: false,
      default: true
    },
    readableEntities: {
      type: Array,
      required: true,
      default: () => []
    },
    readableFacilities: {
      type: Array,
      required: true,
      default: () => []
    }
  },
  components: { MeasurementDetailsModal, Multiselect, RangeDatePicker, LoadingAnimation},
  mixins: [
    TableQueryMixin,
    MultiselectLabelFormatter,
    ClustersAndEntitiesMixin
  ],
  async mounted() {
  },
  data() {
    return {
      hasMadeFirstLoad: false,
      isLoading: false,
      filters: {
        facility: null,
        appointmentStatus: null,
        dates: {
          start: null,
          end: null,
        },
      },
      appointmentStatuses: [
        {
          id: 1,
          slug: 'ongoing',
        },
        {
          id: 3,
          slug: 'closed',
        },
        {
          id: 99,
          slug: 'automaticallyClosed',
        }
      ],
      queryUrl: `${OneCareService.baseURL}/appointments`,
      columns: ['createdAt', 'updatedAt', 'begin', 'end', 'status', 'patient', 'tests'],
      tableOptions: {
        requestFunction: (data) => {
          const page = data.page
          return new Promise((resolve, reject) => {
            if (this.cancelationCallback !== null) {
              this.cancelationCallback()
            }
            this.isLoading = true
            const limit = this.$refs.checkupPlusEvaluationsTable ? this.$refs.checkupPlusEvaluationsTable.limit : 10
            const queryUrl = this.queryUrl
            data.pageSize=limit
            data.byBegin = false
            data.byEnd = false
            data.byUpdate = true
            data.order_desc = data.ascending !== 1

            if (!this.hasMadeFirstLoad) {
              data.from = null
              data.to = null
              data.order_desc = true
              data.status=''
            }
            if (Object.keys(data).includes('facilityId') && data.facilityId === '') {
              data.facilityId = null
            }
            if (Object.keys(data).includes('from') && data.from === '') {
              data.from = null
              data.to = null
            }

            /*if (data.orderBy === 'updated') {
              data.by_updated = true
              data.by_created = false
            } else if (data.orderBy === 'created') {
              data.by_created = true
              data.by_updated = false
            } else if (data.orderBy === null) {
              data.by_created = false
              data.by_updated = false
            }*/

            ApiService.cancelableQuery(queryUrl, data, new axios.CancelToken(c => {
              this.cancelationCallback = c
            }))
              .then(response => {
                if (response.data.page) {
                  if (this.getTableReference) {
                    this.getTableReference().$refs.pagination.Page = response.data.page
                  }
                } else if (page) {
                  if (this.getTableReference) {
                    this.getTableReference().$refs.pagination.Page = page
                  }
                }
                this.hasMadeFirstLoad = true
                resolve({
                  data: response.data.objects ? response.data.objects : [],
                  count: response.data.totalItems ? response.data.totalItems : 0,
                  page: response.data.page ? response.data.page : 1
                })
              })
              .catch(error => {
                if (!axios.isCancel(error)) {
                  reject(error)
                }
              })
              .finally(() => {
                this.isLoading = false
              })
          })
        },
        sortable: ['updatedAt'],
        descOrderColumns: ['updatedAt'],
        orderBy: {
          'ascending': false,
          'column': 'updatedAt'
        },
        filterable: true,
        customFilters: ['from', 'to', 'status', 'facilityId'],
        skin: 'table table-striped table-hover is-fullwidth',
        headings: {
          createdAt: this.$t('ranu.records.created_at'),
          updatedAt: this.$t('ranu.records.updated_at'),
          begin: this.$t('agenda.availability.start'),
          end: this.$t('agenda.availability.end'),
          status: this.$t('checkup_plus.evaluations.appointment_status'),
          patient: this.$t('agenda.appointments.pacient'),
          tests: this.$t('kiosks.graph.tests')
        },
      },
      selectedAppointment: null
    }
  },
  methods: {
    handleRowClick: function(row) {
      console.log(row)
      this.selectedAppointment = row
    },
    handleMeasurementDetailsModalClose: function() {
      this.selectedAppointment = null
    },
    getTableReference: function () {
      return this.$refs.checkupPlusEvaluationsTable
    },
    distinctMeasurementGroupsInAppointment: function(measurementGroup) {
      const measGroupIds = measurementGroup.map(elem => elem.measurementGroup.idMeasurementGroup)
      return [...new Set(measGroupIds)];
    },
    handleIconStr: function(measGroupId) {
      const measurementTypeInfo = this.measurementTypesInfo.find(info => info.measurementGroupId === measGroupId)
      if(!measurementTypeInfo) {
        console.log("Cannot find info for meas group id: "+measGroupId)
        return ''
      }
      const iconName = measurementTypeInfo.iconName
      return `i-checkup-${iconName}`
    },
    handleRefreshTable: function() {
      this.selectedAppointment = null
      this.$refs.checkupPlusEvaluationsTable.refresh()
    }
  },
  computed: {
    measurementTypesInfo: function () {
      return this.$store.state.checkup.measurementTypesInfo
    },
  },
  watch: {
    'filters.facility': {
      handler: function (options) {
        if (options !== null) {
          this.emitTableFilter('vue-tables.checkupPlusEvaluationsTable.filter::facilityId', options.id)
        } else {
          if (this.getTableReference() !== undefined) {
            this.emitTableFilter('vue-tables.checkupPlusEvaluationsTable.filter::facilityId', null)
          }
        }
      },
      immediate: true,
      deep: true,
    },
    'filters.dates': {
      handler: function (value) {
        if (this.getTableReference() !== undefined) {
          if (value.start === null || value.end === null) {
            this.getTableReference().orderBy = {
              'ascending': false,
              'column': false
            }

            this.getTableReference().customQueries.from = null
            this.emitTableFilter('vue-tables.checkupPlusEvaluationsTable.filter::to', null)
          } else {
            if (this.getTableReference().orderBy.column === false) {
              this.getTableReference().orderBy = {
                'ascending': false,
                'column': 'updated'
              }
            }
            this.getTableReference().customQueries.from = this.moment(value.start).format('YYYY/MM/DD') || ''
            this.emitTableFilter('vue-tables.checkupPlusEvaluationsTable.filter::to', this.moment(value.end).format('YYYY/MM/DD') || '')
          }
        }
      },
      immediate: true,
      deep: true,
    },
    'filters.appointmentStatus': {
      handler: function (value) {
        this.emitTableFilter('vue-tables.checkupPlusEvaluationsTable.filter::status', value ? value.id : "")
      },
      immediate: false,
      deep: true,
    }
  }
};
</script>

<style scoped lang="scss">

</style>
